import { useState, useCallback } from 'react';
import { uploadFileWithValidation, deleteFile } from './uploadService';

export const useFileUpload = (options = {}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const uploadFile = useCallback(
    async (file) => {
      setUploading(true);
      setError(null);
      setProgress(0);

      try {
        const result = await uploadFileWithValidation(file, options, (progressPercent) => {
          setProgress(progressPercent);
        });

        if (result.success) {
          const newFile = {
            id: result.data.public_id,
            url: result.data.url,
            name: file.name,
            size: result.data.size,
            format: result.data.format,
            uploadedAt: new Date().toISOString(),
          };

          setUploadedFiles((prev) => [...prev, newFile]);
          return newFile;
        } else {
          throw new Error(result.message || 'Upload failed');
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [options]
  );

  const removeFile = useCallback(async (fileId) => {
    try {
      await deleteFile(fileId);
      setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
    setUploadedFiles([]);
  }, []);

  return {
    uploadFile,
    removeFile,
    clearFiles,
    reset,
    uploading,
    progress,
    error,
    uploadedFiles,
  };
};
